import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Share2} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { getBlogPostBySlug } from '../data/blogData';
import Footer from './Footer';

interface BlogDetailProps {
  slug: string;
}

const BlogDetail: React.FC<BlogDetailProps> = ({ slug }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const post = getBlogPostBySlug(slug);

  if (!post) {
    return (
      <div className={`min-h-screen transition-colors duration-500 ${
        isDark 
          ? 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 text-gray-100'
          : 'bg-gradient-to-br from-slate-200/50 via-slate-100/70 to-slate-200/50 text-slate-800'
      } pt-20 pb-16 flex items-center justify-center`}>
        <div className={`glass border rounded-3xl p-12 text-center ${
          isDark ? 'glass-dark' : 'glass-light'
        }`}>
          <h1 className={`text-3xl font-light mb-4 ${
            isDark ? 'text-gray-100' : 'text-slate-800'
          }`}>
            Blog Post Not Found
          </h1>
          <p className={`text-lg ${
            isDark ? 'text-gray-300' : 'text-slate-600'
          }`}>
            The blog post you're looking for doesn't exist.
          </p>
          <Link
            to="/blog"
            className={`mt-6 glass border px-6 py-3 rounded-full transition-all duration-300 hover:scale-105 font-medium ${
              isDark 
                ? 'glass-dark glass-hover-dark text-gray-100' 
                : 'glass-light glass-hover-light text-slate-700'
            }`}
          >
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // You could add a toast notification here
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 ${
      isDark 
        ? 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 text-gray-100'
        : 'bg-gradient-to-br from-slate-200/50 via-slate-100/70 to-slate-200/50 text-slate-800'
    } pt-20 pb-16`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-8">
          <Link
            to="/blog"
            className={`group glass border rounded-full px-6 py-3 transition-all duration-300 hover:scale-105 font-medium ${
              isDark 
                ? 'glass-dark glass-hover-dark text-gray-100' 
                : 'glass-light glass-hover-light text-slate-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform duration-300" />
              <span>Back to Blog</span>
            </div>
          </Link>
        </div>

        {/* Hero Image */}
        <div className={`glass border rounded-3xl overflow-hidden mb-8 ${
          isDark ? 'glass-dark' : 'glass-light'
        }`}>
          <div className="relative h-64 md:h-80">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark 
                    ? 'bg-sunrise-orange/20 text-sunrise-orange border border-sunrise-orange/30' 
                    : 'bg-deep-blue/10 text-deep-blue border border-deep-blue/20'
                }`}>
                  {post.category}
                </span>
              </div>
              <h1 className={`text-3xl md:text-4xl font-light leading-tight ${
                isDark ? 'text-gray-100' : 'text-white'
              }`}>
                {post.title}
              </h1>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className={`glass border rounded-3xl p-8 md:p-12 ${
              isDark ? 'glass-dark' : 'glass-light'
            }`}>
              {/* Meta Information */}
              <div className="flex flex-wrap items-center gap-6 mb-8 pb-6 border-b border-gray-200 dark:border-gray-700">
                <div className={`flex items-center gap-2 text-sm ${
                  isDark ? 'text-gray-400' : 'text-slate-500'
                }`}>
                  <User size={16} />
                  <span>{post.author}</span>
                </div>
                <div className={`flex items-center gap-2 text-sm ${
                  isDark ? 'text-gray-400' : 'text-slate-500'
                }`}>
                  <Calendar size={16} />
                  <span>{new Date(post.date).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
                <div className={`flex items-center gap-2 text-sm ${
                  isDark ? 'text-gray-400' : 'text-slate-500'
                }`}>
                  <Clock size={16} />
                  <span>{post.readTime}</span>
                </div>
                <button
                  onClick={handleShare}
                  className={`flex items-center gap-2 text-sm transition-colors duration-300 hover:text-sunrise-orange ${
                    isDark ? 'text-gray-400' : 'text-slate-500'
                  }`}
                >
                  <Share2 size={16} />
                  <span>Share</span>
                </button>
              </div>

              {/* Excerpt */}
              <div className={`text-lg leading-relaxed mb-8 ${
                isDark ? 'text-gray-200' : 'text-slate-600'
              }`}>
                {post.excerpt}
              </div>

              {/* Content */}
              <div 
                className={`prose prose-lg max-w-none ${
                  isDark ? 'prose-invert' : 'prose-slate'
                }`}
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              
              {/* <div className={`glass border rounded-2xl p-6 ${
                isDark ? 'glass-dark' : 'glass-light'
              }`}>
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full overflow-hidden">
                    <img
                      src="/profile-picture.jpg"
                      alt={post.author}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className={`text-lg font-semibold mb-2 ${
                    isDark ? 'text-gray-100' : 'text-slate-800'
                  }`}>
                    {post.author}
                  </h3>
                  <p className={`text-sm ${
                    isDark ? 'text-gray-300' : 'text-slate-600'
                  }`}>
                    Strategic Communications Consultant
                  </p>
                </div>
              </div> */}

              {/* Tags */}
              <div className={`glass border rounded-2xl p-6 ${
                isDark ? 'glass-dark' : 'glass-light'
              }`}>
                <h4 className={`text-lg font-semibold mb-4 ${
                  isDark ? 'text-gray-100' : 'text-slate-800'
                }`}>
                  Tags
                </h4>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className={`px-3 py-1 rounded-full text-sm ${
                        isDark 
                          ? 'bg-gray-800/50 text-gray-300' 
                          : 'bg-gray-100 text-slate-600'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Reading Progress */}
              {/* <div className={`glass border rounded-2xl p-6 ${
                isDark ? 'glass-dark' : 'glass-light'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen size={16} className="text-sunrise-orange" />
                  <span className={`text-sm font-medium ${
                    isDark ? 'text-gray-300' : 'text-slate-600'
                  }`}>
                    Reading Progress
                  </span>
                </div>
                <div className={`w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2`}>
                  <div className="bg-gradient-to-r from-sunrise-orange to-serengeti-gold h-2 rounded-full w-0 transition-all duration-300" id="reading-progress"></div>
                </div>
              </div> */}
            </div>
          </div>
        </div>

        {/* Related Posts */}
        {/* <div className="mt-16">
          <h2 className={`text-2xl font-light mb-8 text-center ${
            isDark ? 'text-gray-100' : 'text-slate-800'
          }`}>
            More Insights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
          </div>
        </div> */}
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BlogDetail;
